/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import e from "../../Color.js";
import { isSymbol3D as t, isSymbol2D as o } from "../../symbols.js";
import { forEach as n } from "../../core/asyncUtils.js";
import "../../core/has.js";
import { px2pt as r } from "../../core/screenUtils.js";
import { O as l } from "../../chunks/vec3f64.js";
import { effectFunctionsFromJSON as i } from "../../layers/effects/jsonUtils.js";
import { getCIMSymbolColor as c } from "./cimSymbolUtils.js";
import { getStroke as s } from "./gfxUtils.js";
import { Symbol3DMaterial as u } from "./Symbol3DMaterial.js";
const a = new e("white");
function f(e) {
  const t = e.symbolLayers?.at(-1);
  if (t && "outline" in t) return t?.outline?.size;
}
function m(e) {
  if (!e) return 0;
  if (t(e)) {
    const t = f(e);
    return null != t ? t : 0;
  }
  return r(s(e)?.width);
}
function y(e) {
  if (null == e || !("symbolLayers" in e) || null == e.symbolLayers) return !1;
  switch (e.type) {
    case "point-3d":
      return e.symbolLayers.some(e => "object" === e.type);
    case "line-3d":
      return e.symbolLayers.some(e => "path" === e.type);
    case "polygon-3d":
      return e.symbolLayers.some(e => "object" === e.type || "extrude" === e.type);
    default:
      return !1;
  }
}
function p(e) {
  return e.resource?.href ?? "";
}
function h(n, r) {
  if (!n) return null;
  let l = null;
  return t(n) ? l = b(n) : o(n) && (l = "cim" === n.type ? c(n) : n.color ? new e(n.color) : null), l ? w(l, r) : null;
}
function b(t) {
  const o = t.symbolLayers;
  if (!o) return null;
  let n = null;
  return o.forEach(e => {
    "object" === e.type && e.resource?.href || (n = "water" === e.type ? e.color : e.material ? e.material.color : null);
  }), n ? new e(n) : null;
}
function w(t, o) {
  if (null == o || null == t) return t;
  const n = t.toRgba();
  return n[3] = n[3] * o, new e(n);
}
function d(e, t, o) {
  const n = e.symbolLayers;
  if (!n) return;
  const r = e => w(t = t ?? e ?? (null != o ? a : null), o);
  n.forEach(e => {
    if ("object" !== e.type || !e.resource?.href || t) if ("water" === e.type) e.color = r(e.color);else {
      const t = null != e.material ? e.material.color : null,
        n = r(t);
      null == e.material ? e.material = new u({
        color: n
      }) : e.material.color = n, null != o && "outline" in e && null != e.outline?.color && (e.outline.color = w(e.outline.color, o));
    }
  });
}
function j(e, t, o) {
  (t = t ?? e.color) && (e.color = w(t, o)), null != o && "outline" in e && e.outline?.color && (e.outline.color = w(e.outline.color, o));
}
function k(n, r, l) {
  n && (r || null != l) && (r && (r = new e(r)), t(n) ? d(n, r, l) : o(n) && j(n, r, l));
}
async function g(e, t) {
  const o = e.symbolLayers;
  o && (await n(o, async e => L(e, t)));
}
async function L(e, t) {
  switch (e.type) {
    case "extrude":
      x(e, t);
      break;
    case "icon":
    case "line":
    case "text":
      z(e, t);
      break;
    case "path":
      v(e, t);
      break;
    case "object":
      await U(e, t);
  }
}
function z(e, t) {
  const o = S(t);
  null != o && (e.size = o);
}
function S(e) {
  for (const t of e) if ("number" == typeof t) return t;
  return null;
}
function x(e, t) {
  e.size = "number" == typeof t[2] ? t[2] : 0;
}
async function U(e, t) {
  const {
      resourceSize: o,
      symbolSize: n
    } = await O(e),
    r = E(t, o, n);
  e.width = C(t[0], n[0], o[0], r), e.depth = C(t[1], n[1], o[1], r), e.height = C(t[2], n[2], o[2], r);
}
function v(e, t) {
  const o = E(t, l, [e.width, void 0, e.height]);
  e.width = C(t[0], e.width, 1, o), e.height = C(t[2], e.height, 1, o);
}
function E(e, t, o) {
  for (let n = 0; n < 3; n++) {
    const r = e[n];
    switch (r) {
      case "symbol-value":
        {
          const e = o[n];
          return null != e ? e / t[n] : 1;
        }
      case "proportional":
        break;
      default:
        if (r && t[n]) return r / t[n];
    }
  }
  return 1;
}
async function O(e) {
  const {
      computeObjectLayerResourceSize: t
    } = await import("./symbolLayerUtils.js"),
    o = await t(e, 10),
    {
      width: n,
      height: r,
      depth: l
    } = e,
    i = [n, l, r];
  let c = 1;
  for (let s = 0; s < 3; s++) {
    const e = i[s];
    if (null != e) {
      c = e / o[s];
      break;
    }
  }
  for (let s = 0; s < 3; s++) null == i[s] && (i[s] = o[s] * c);
  return {
    resourceSize: o,
    symbolSize: i
  };
}
function C(e, t, o, n) {
  switch (e) {
    case "proportional":
      return o * n;
    case "symbol-value":
      return null != t ? t : o;
    default:
      return e;
  }
}
function M(e, t) {
  const o = S(t);
  if (null != o) switch (e.type) {
    case "simple-marker":
      e.size = o;
      break;
    case "picture-marker":
      {
        const t = e.width / e.height;
        t > 1 ? (e.width = o, e.height = o * t) : (e.width = o * t, e.height = o);
        break;
      }
    case "simple-line":
      e.width = o;
      break;
    case "text":
      e.font.size = o;
  }
}
async function R(e, n) {
  if (e && n) return t(e) ? g(e, n) : void (o(e) && M(e, n));
}
function D(e, n, r) {
  if (e && null != n) if (t(e)) {
    const t = e.symbolLayers;
    t && t.forEach(e => {
      if (e && "object" === e.type) switch (r) {
        case "tilt":
          e.tilt = n;
          break;
        case "roll":
          e.roll = n;
          break;
        default:
          e.heading = n;
      }
    });
  } else o(e) && ("simple-marker" !== e.type && "picture-marker" !== e.type && "text" !== e.type || (e.angle = n));
}
function I(e) {
  if (!e) return null;
  const t = e.effects.filter(e => "bloom" !== e.type).map(e => e.toJSON());
  return i(t);
}
function J(e) {
  return null != e && "polygon-3d" === e.type && e.symbolLayers.some(e => "extrude" === e.type);
}
async function N(e, t) {
  const o = await e.fetchSymbol(t);
  return o || e.fetchCIMSymbol(t);
}
export { k as applyColorToSymbol, w as applyOpacityToColor, D as applyRotationToSymbol, R as applySizesToSymbol, N as fetchWebStyleSymbol, I as getCSSFilterFromEffectList, h as getColorFromSymbol, p as getIconHref, m as getSymbolOutlineSize, y as isVolumetricSymbol, J as symbolHasExtrudeSymbolLayer };